import { useId, useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import {
  ActionComments,
  ActionRowGlyph,
  ActionRowHeader,
  EditablePayload,
  ProposalPricebookMatchButton,
  PurchaseOrderMatchButton,
} from "@/accounts-payable/common";
import type { ApAgentAction, ApDocumentType } from "@/accounts-payable/types";
import {
  actionCategoryLabel,
  actionDetail,
  approveLabel,
  getNewItemsToCreate,
  hasEditablePayload,
  isApprovedWithPendingAction,
  isNoOpAction,
  isPendingReviewAction,
  purchaseOrderProposalLink,
} from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";
import { NewPricebookItemsNote } from "./new-pricebook-items-note";
import { RECORD_CARD_CLASS } from "./record-fields";
import { TargetObject } from "./target-object";
/**
 * One agent action on the review page: the sidebar's row header (status glyph,
 * category, confidence, outcome), then the reasoning, the ServiceTitan record
 * in a grey card, comments, and the decision as labelled pills. Pending rows
 * leave the target to the record card so it isn't said twice. A PO carrying
 * new pricebook items says so and names the extra items its approval creates.
 */
export const ActionReviewCard = ({
  action,
  runActions,
  businessId,
  document,
  onResolve,
  onSave,
  resolving,
  saving,
}: {
  action: ApAgentAction;
  /** Every action of the action's run, to find the new items a PO carries. */
  runActions: ApAgentAction[];
  businessId: string | null;
  /**
   * The document the action is for, and the run it came from. With it, a PO
   * or pricebook proposal offers choosing the PO or the line's item by hand
   * instead.
   */
  document?: {
    id: string;
    type: ApDocumentType;
    agentRunId: string | null;
    /** Whether the document has a job, whose POs the PO picker opens on. */
    hasJob: boolean;
  };
  onResolve: (decision: "approved" | "rejected") => void;
  onSave: (payload: ApAgentAction["payload"]) => void;
  resolving?: boolean;
  saving?: boolean;
}) => {
  const pending = isPendingReviewAction(action);
  const noOp = isNoOpAction(action);
  // Every card has the same three buttons, so each one's name says which
  // action it decides ("Approve: Lower cost").
  const subject = noOp ? actionDetail(action) : actionCategoryLabel(action);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<ApAgentAction["payload"]>(action.payload);

  const startEdit = () => {
    setDraft({ ...action.payload });
    setEditing(true);
  };
  const save = () => {
    onSave(draft);
    setEditing(false);
  };
  const disabled = resolving || saving;
  // Whether the API would refuse to approve, and the new pricebook items the
  // approval creates: both straight from the API, so the card shows what it
  // enforces. A blocked PO creates nothing, so it lists no items.
  const blockedReason = pending ? action.approval_blocked_reason : null;
  const approveBlocked = Boolean(blockedReason);
  const newItems =
    pending && !approveBlocked ? getNewItemsToCreate(action, runActions) : [];
  // Ties the disabled Approve button to the reason shown in the note.
  const blockedReasonId = useId();
  const poLink = document
    ? purchaseOrderProposalLink(action, document.hasJob)
    : null;

  const controls = pending ? (
    editing ? (
      <Stack direction="row" gap="xs" align="center">
        <Button
          size="xsPill"
          variant="secondary"
          onClick={save}
          loading={saving}
          disabled={disabled}
        >
          Save changes
        </Button>
        <Button
          size="xsPill"
          variant="text"
          disabled={disabled}
          onClick={() => setEditing(false)}
        >
          Cancel
        </Button>
      </Stack>
    ) : (
      <Stack direction="row" gap="xs" align="center" className="flex-wrap">
        {/* Labelled pills, one size: the primary in green, the rest grey. On a
            flag, approving records it seen and rejecting records it ignored. */}
        <Button
          size="xsPill"
          variant={noOp ? "tertiary" : "secondary"}
          onClick={() => onResolve("approved")}
          disabled={disabled || approveBlocked}
          aria-label={`${noOp ? "Mark as seen" : approveLabel(action, runActions)}: ${subject}`}
          aria-describedby={approveBlocked ? blockedReasonId : undefined}
        >
          {noOp ? "Mark as seen" : approveLabel(action, runActions)}
        </Button>
        {hasEditablePayload(action) && (
          <Button
            size="xsPill"
            variant="tertiary"
            onClick={startEdit}
            disabled={disabled}
            aria-label={`Edit: ${subject}`}
          >
            Edit
          </Button>
        )}
        <Button
          size="xsPill"
          variant="tertiary"
          onClick={() => onResolve("rejected")}
          disabled={disabled}
          aria-label={`${noOp ? "Ignore" : "Dismiss"}: ${subject}`}
        >
          {noOp ? "Ignore" : "Dismiss"}
        </Button>
        {/* The PO is wrong rather than the proposal's details: choosing it
            supersedes this proposal and has the agent re-check. */}
        {document && poLink && (
          <PurchaseOrderMatchButton
            businessId={businessId}
            documentId={document.id}
            documentType={document.type}
            agentRunId={document.agentRunId}
            hasJob={poLink.hasJob}
            label={poLink.label}
            variant="tertiary"
          />
        )}
        {/* Likewise when the proposal is aimed at the wrong pricebook item:
            choosing the line's item supersedes it. */}
        {document && (
          <ProposalPricebookMatchButton
            businessId={businessId}
            documentId={document.id}
            documentType={document.type}
            agentRunId={document.agentRunId}
            action={action}
          />
        )}
      </Stack>
    )
  ) : null;

  return (
    <Stack direction="row" gap="sm" align="start" fullWidth className="p-4">
      <ActionRowGlyph action={action} />
      <Stack gap="md" className="min-w-0 flex-1">
        <ActionRowHeader action={action} showTarget={!pending} />

        {/* Why, before what: the reasoning leads, the record follows. */}
        {pending &&
          action.rationale &&
          action.rationale !== actionDetail(action) && (
            <Typography textColor="secondary">{action.rationale}</Typography>
          )}

        {pending &&
          (editing ? (
            <Stack
              gap="xs"
              fullWidth
              className={cn(RECORD_CARD_CLASS, "text-md")}
            >
              <Typography variant="captionW1" textColor="tertiary">
                Editing
              </Typography>
              <EditablePayload
                action={action}
                draft={draft}
                setDraft={setDraft}
              />
            </Stack>
          ) : (
            <TargetObject action={action} />
          ))}

        {(newItems.length > 0 || blockedReason) && !editing && (
          <NewPricebookItemsNote
            items={newItems}
            blockedReason={blockedReason}
            blockedReasonId={blockedReasonId}
          />
        )}

        {pending && isApprovedWithPendingAction(action, runActions) && (
          <Typography variant="captionW1" textColor="secondary">
            This item goes on the purchase order in this run. Approving that PO
            approves this item too.
          </Typography>
        )}

        {/* A note is part of reaching the decision, so it sits above it; the
            decision closes the card. */}
        <ActionComments action={action} businessId={businessId} />
        {controls}
      </Stack>
    </Stack>
  );
};
